'use client'

import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import {
    getIntegrations,
    deleteIntegration,
} from '@/lib/api/integrations/integrations-api'
import { Integration } from '@/lib/api/common/interfaces/integration.interface'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdIntegrationInstructions,
    MdArrowForward,
    MdClose,
    MdLock,
    MdFileDownload,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import ConfirmModal from '@/components/confirm-modal'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import PaginationControls from '@/components/pagination/PaginationControls'
import { ShareableModule } from '@/lib/api/share/share-api'

export default function IntegrationsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'atendimento',
        'integrations'
    )
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [integrationToDelete, setIntegrationToDelete] = useState<
        number | null
    >(null)
    const [deleting, setDeleting] = useState(false)
    const [showImportModal, setShowImportModal] = useState(false)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)

    const filters = useMemo(
        () => ({
            companyId: user?.companyId ?? undefined,
            search: debouncedSearch.trim() || undefined,
        }),
        [user?.companyId, debouncedSearch]
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getIntegrations(params)
        },
        []
    )

    const {
        items: displayedIntegrations,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Integration, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleDelete = (integrationId: number) => {
        setIntegrationToDelete(integrationId)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!integrationToDelete) return

        setDeleting(true)
        try {
            const response = await deleteIntegration(integrationToDelete)
            if (response.success) {
                toast.success('Integração excluída com sucesso')
                reload()
                setShowDeleteModal(false)
                setIntegrationToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir integração')
            }
        } catch (error) {
            toast.error('Erro ao excluir integração')
        } finally {
            setDeleting(false)
        }
    }

    if (loading && displayedIntegrations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando integrações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdIntegrationInstructions className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Integrações
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'integração' : 'integrações'}{' '}
                            cadastradas
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() => setShowImportModal(true)}
                        >
                            <MdFileDownload className="text-xl" />
                            <span className="hidden sm:inline">Importar</span>
                        </button>
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() => navigate('/integrations/new')}
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Adicionar</span>
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <form
                    onSubmit={handleSearch}
                    className="flex items-center gap-2"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, descrição ou contexto..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </form>
            </div>

            {displayedIntegrations.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdIntegrationInstructions className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma integração encontrada'
                                : 'Nenhuma integração cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando integrações'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() => navigate('/integrations/new')}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeira integração
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {displayedIntegrations.map(integration => (
                        <div
                            key={integration.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-xl flex items-center justify-center border border-secondary/20">
                                    <MdIntegrationInstructions className="text-3xl text-secondary" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-base font-bold text-base-content break-words">
                                                {integration.name}
                                            </h3>
                                            {integration.description && (
                                                <p className="text-sm text-base-content/70 mt-1 line-clamp-2 break-words">
                                                    {integration.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                        {integration.authenticationType !==
                                            'NONE' && (
                                            <div className="flex items-center gap-1 text-xs text-base-content/60">
                                                <MdLock className="text-sm" />
                                                <span>
                                                    {
                                                        integration.authenticationType
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2 text-xs text-base-content/60">
                                            <span>
                                                Criado em{' '}
                                                {new Date(
                                                    integration.createdAt
                                                ).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>

                                        {(canUpdate || canDelete) && (
                                            <div className="flex items-center gap-2">
                                                {canUpdate && (
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/integrations/edit/${integration.id}`
                                                            )
                                                        }
                                                        className="btn btn-sm btn-ghost"
                                                        title="Editar"
                                                    >
                                                        <MdEdit className="text-lg" />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(
                                                                integration.id
                                                            )
                                                        }
                                                        className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                                        title="Excluir"
                                                    >
                                                        <MdDelete className="text-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="integração"
                        />
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setIntegrationToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir integração"
                message="Tem certeza de que deseja excluir esta integração? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.INTEGRATION}
                moduleLabel="Integrações"
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response = await getIntegrations(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: Integration) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.name}
                        </p>
                        {item.description && (
                            <p className="text-xs text-base-content/60 truncate">
                                {item.description}
                            </p>
                        )}
                    </div>
                )}
                getItemId={(item: Integration) => item.id}
                getItemName={(item: Integration) => item.name}
                onImportComplete={reload}
            />
        </div>
    )
}
